import { Flex } from "../components/Flex";
import { Box } from "../components/Box";
import { Image } from "../components/Image";
import { Input } from "../components/Input";
import { H1 } from "../components/Heading";
import { Paragraph } from "../components/Paragraph";
import { Button } from "../components/Button";
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import loginSvg from "../assets/login.svg";
import tillerVerifySvg from "../assets/verify-tiller.svg";
import { useAuth } from "../context/AuthContext";
import { ROUTE } from "../routes";
import { FeatureFlagKey, isEnabled } from "../feature-flags";
import { Form, Formik } from "formik";
import { loginValidation } from "../utils/validationSchema";
import { useEffect } from "react";
import { getSafeReturnUrl } from "../utils/helpers";
import { STORAGE_KEYS } from "../utils/constants";
import { useDefaultLandingRoute } from "../hooks/useDefaultLandingRoute";

const Login = () => {
  const { isLoggedIn, login, userDetails, isLoadingUserDetails } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { defaultRoute, isLoading: isLoadingLanding } = useDefaultLandingRoute(
    Boolean(isLoggedIn && userDetails),
  );

  const qsReturn = searchParams.get("returnurl") || "";

  useEffect(() => {
    if (!qsReturn) {
      window.sessionStorage.removeItem(STORAGE_KEYS.pendingReturnUrl);
    }
  }, [qsReturn]);

  const safeReturnUrl = getSafeReturnUrl(qsReturn);
  const asQueryValue = safeReturnUrl
    ? encodeURIComponent(safeReturnUrl.replace(/^\//, ""))
    : "";

  const onForgotPasswordClick = () => {
    const suffix = asQueryValue ? `?returnurl=${asQueryValue}` : "";
    navigate(`${ROUTE.RESET_PASSWORD}${suffix}`);
  };

  const handleLogin = async (email: string, password: string) => {
    await login.call(email, password, `${location.pathname}${location.search}`);
  };

  // Post-login routing logic
  if (isLoggedIn && !isLoadingUserDetails && userDetails) {
    if (userDetails?.two_factor_enabled) {
      const suffix = asQueryValue ? `?returnurl=${asQueryValue}` : "";
      return <Navigate to={`${ROUTE.LOGIN_2FA}${suffix}`} replace />;
    }

    if (userDetails?.two_factor_enabled === false) {
      if (isLoadingLanding) return null;

      window.sessionStorage.removeItem(STORAGE_KEYS.pendingReturnUrl);

      // A return URL into a module the user can't view is caught by that
      // module's route guard, which sends them on to defaultRoute.
      return <Navigate to={safeReturnUrl || defaultRoute} replace />;
    }

    const suffix = asQueryValue ? `?returnurl=${asQueryValue}` : "";
    return <Navigate to={`${ROUTE.SETUP2FA}${suffix}`} replace />;
  }

  return (
    <Flex height="100vh">
      <Flex
        width="900px"
        justifyContent="space-between"
        flexDirection="column"
        p="60px"
        overflowY="auto"
      >
        <Image
          src={tillerVerifySvg}
          alt="tiller verify logo"
          height="200px"
          width="160px"
        />

        <Formik
          initialValues={{
            email: "",
            password: "",
          }}
          validationSchema={loginValidation}
          onSubmit={({ email, password }) => {
            handleLogin(email, password);
          }}
        >
          {(props) => (
            <Form onSubmit={props.handleSubmit}>
              <Box>
                <H1 color="darkBlue">Welcome Back</H1>
                <Paragraph color="darkBlue">
                  Sign into your account to continue
                </Paragraph>

                <Input
                  onChange={props.handleChange}
                  value={props.values.email}
                  placeholder="Enter your email address"
                  mt="4"
                  label="Email Address"
                  name="email"
                  hasError={Boolean(props.errors?.email)}
                  errorMessage={props.errors?.email}
                  isRequired
                />

                <Input
                  onChange={props.handleChange}
                  value={props.values.password}
                  type="password"
                  placeholder="Enter your password"
                  mt="2"
                  label="Password"
                  name="password"
                  hasError={Boolean(props.errors?.password)}
                  errorMessage={props.errors?.password}
                  isRequired
                />

                <Box height="24px" my="2">
                  {login.errors?.detail && (
                    <Paragraph my="0px" fontSize="1" color="red.primary">
                      {login.errors?.detail}
                    </Paragraph>
                  )}
                </Box>
              </Box>

              <Box>
                <Button
                  type="submit"
                  isDisabled={
                    !props.dirty ||
                    (props.dirty && !props.isValid) ||
                    login.isLoading
                  }
                  mt="2"
                  size="large"
                  width="100%"
                >
                  Sign In
                </Button>

                {isEnabled(FeatureFlagKey.RESET_PASSWORD) && (
                  <Button
                    variant="text"
                    onClick={onForgotPasswordClick}
                    size="large"
                    mt="2"
                    width="100%"
                  >
                    Forgot password?
                  </Button>
                )}
              </Box>

              <Paragraph mt="4" color="darkBlue">
                <i>
                  If you received a request to complete the verification
                  process, please download the Verify by Tiller mobile app using
                  the link in your invitation email.
                </i>
              </Paragraph>
            </Form>
          )}
        </Formik>
      </Flex>

      <Flex
        bg={"background"}
        display={{ _: "none", md: "flex" }}
        height="100vh"
        width="100%"
        justifyContent="center"
        alignItems="center"
      >
        <Image
          src={loginSvg}
          alt="login image"
          display="block"
          width="55%"
          height="60%"
        />
      </Flex>
    </Flex>
  );
};

export default Login;
